<template>
  <f7-block class="campaign-lifecycle-bar" v-if="show">
    <div class="lifecycle-header display-flex align-items-center justify-content-space-between mb-3">
      <div class="display-flex align-items-center gap-2">
        <div class="lifecycle-icon-circle">
          <f7-icon material="account_tree" :size="20" />
        </div>
        <div>
          <h3 class="font-label-lg text-label-lg text-on-surface font-semibold">{{ $t('campaign.lifecycleBar.title') }}</h3>
          <p class="font-label-sm text-label-sm text-on-surface-variant">{{ $t('campaign.lifecycleBar.stepOf', { current: currentStageIndex + 1, total: totalStages }) }}</p>
        </div>
      </div>
    </div>

    <!-- Horizontal Pipeline Stepper -->
    <div class="pipeline-stepper overflow-x-auto pb-2">
      <div v-for="(stage, i) in stages" :key="stage.key" class="pipeline-stage flex-shrink-0 display-flex flex-direction-column gap-1.5" :class="{ current: i === currentStageIndex, completed: i < currentStageIndex, upcoming: i > currentStageIndex }">
        <div class="stage-dot-wrapper display-flex align-items-center justify-content-center">
          <span class="stage-dot display-flex align-items-center justify-content-center" :class="dotClass(i)">
            <f7-icon v-if="i < currentStageIndex" material="done" :size="14" class="text-on-primary" />
            <f7-icon v-else-if="i === currentStageIndex" material="radio_button_unchecked" :size="14" class="text-on-primary" />
            <span v-else class="font-label-sm text-label-sm font-bold">{{ i + 1 }}</span>
          </span>
        </div>
        <span class="stage-label font-label-md text-label-md text-on-surface text-align-center" :class="{ 'font-bold text-primary': i <= currentStageIndex, 'text-on-surface-variant': i > currentStageIndex }">{{ $t(stage.labelKey) }}</span>
        <span class="stage-sub font-label-sm text-label-sm text-align-center" :class="{ 'text-on-surface-variant': i <= currentStageIndex, 'text-outline': i > currentStageIndex }">{{ $t(stage.subKey) }}</span>
      </div>
      <!-- Connecting lines between stages -->
      <div v-for="i in stages.length - 1" :key="`line-${i}`" class="pipeline-line flex-1 h-1.5" :class="{ 'bg-primary': i - 1 < currentStageIndex, 'bg-surface-container-highest': i - 1 >= currentStageIndex }" />
    </div>

    <!-- Trust Strip -->
    <f7-block class="lifecycle-trust-strip display-flex align-items-flex-start gap-2.5 mt-4" strong inset>
      <div class="lifecycle-icon-circle lifecycle-icon-circle--lg">
        <f7-icon material="gavel" :size="22" />
      </div>
      <div class="flex-1">
        <div class="display-flex align-items-center gap-1.5">
          <span class="font-label-md text-label-md text-on-surface font-semibold">{{ $t('campaign.lifecycleBar.trustTitle') }}</span>
          <f7-icon material="verified" :size="14" class="text-primary" />
        </div>
        <p class="font-label-sm text-label-sm text-on-surface-variant leading-snug mt-1">{{ $t('campaign.lifecycleBar.trustText') }}</p>
      </div>
    </f7-block>
  </f7-block>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  status: { type: String, required: true }, // DRAFT, SETUP_REQUIRED, READY_TO_PUBLISH, PENDING_VERIFICATION, ACTIVE, PAUSED, COMPLETED, FAILED, CANCELLED, EXPIRED, ARCHIVED
  canManage: { type: Boolean, default: false },
})

const stages = [
  { key: 'DRAFT', labelKey: 'campaign.lifecycle.DRAFT', subKey: 'campaign.lifecycleBar.stageSetup' },
  { key: 'SETUP_REQUIRED', labelKey: 'campaign.lifecycle.SETUP_REQUIRED', subKey: 'campaign.lifecycleBar.stagePublish' },
  { key: 'READY_TO_PUBLISH', labelKey: 'campaign.lifecycle.READY_TO_PUBLISH', subKey: 'campaign.lifecycleBar.stageVerification' },
  { key: 'PENDING_VERIFICATION', labelKey: 'campaign.lifecycle.PENDING_VERIFICATION', subKey: 'campaign.lifecycleBar.stageActive' },
  { key: 'ACTIVE', labelKey: 'campaign.lifecycle.ACTIVE', subKey: 'campaign.lifecycleBar.stageClosing' },
  { key: 'COMPLETED', labelKey: 'campaign.lifecycle.COMPLETED', subKey: 'campaign.lifecycleBar.stageClosing' },
]

const stageOrder = { DRAFT: 0, SETUP_REQUIRED: 1, READY_TO_PUBLISH: 2, PENDING_VERIFICATION: 3, ACTIVE: 4, PAUSED: 4, COMPLETED: 5, FAILED: 5, CANCELLED: 5, EXPIRED: 5, ARCHIVED: 5 }

const currentStageIndex = computed(() => {
  const idx = stageOrder[props.status]
  return idx !== undefined ? idx : 0
})

const totalStages = stages.length

const show = computed(() => props.canManage && ['DRAFT', 'SETUP_REQUIRED', 'READY_TO_PUBLISH', 'PENDING_VERIFICATION', 'ACTIVE', 'PAUSED'].includes(props.status))

function dotClass(i) {
  if (i < currentStageIndex.value) return 'bg-primary'
  if (i === currentStageIndex.value) return 'bg-primary border-2 border-white'
  return 'bg-surface-container-highest'
}
</script>

<style scoped>
.campaign-lifecycle-bar {
  background: var(--f7-surface-container, #f5f5f5);
  border-radius: 16px;
  border: 1px solid var(--f7-list-item-border-color, #e0e0e0);
}

/* Icon circles — bg-primary/10 was a missing utility (transparent circles) */
.lifecycle-icon-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--f7-theme-color) 12%, var(--f7-card-bg-color, #fff));
  color: var(--f7-theme-color, #1a6b42);
}
.lifecycle-icon-circle--lg {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  margin-top: 2px;
}

.pipeline-stepper {
  display: flex;
  align-items: flex-start;
  position: relative;
  padding: 0 8px;
}

.pipeline-stage {
  flex: 1;
  min-width: 60px;
  max-width: 80px;
  position: relative;
  z-index: 2;
}

.stage-dot-wrapper {
  position: relative;
  height: 28px;
}

.stage-dot {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid var(--f7-list-item-border-color, #e0e0e0);
  background: var(--f7-card-bg-color, #fff);
  color: var(--f7-on-surface-variant, #8e8e93);
  z-index: 2;
  transition: all 0.3s ease;
}

.stage-dot.bg-primary {
  background: var(--f7-theme-color);
  border-color: var(--f7-theme-color);
}

.stage-dot.border-white {
  border-color: var(--f7-page-bg-color, #fff);
}

.pipeline-line {
  position: relative;
  z-index: 1;
  margin-top: 12px;
  margin-left: -50%;
  margin-right: -50%;
  flex: 1;
  height: 6px;
  border-radius: 3px;
}

.pipeline-line.bg-primary {
  background: var(--f7-theme-color);
}

.pipeline-line.bg-surface-container-highest {
  background: var(--f7-surface-container-highest, #dce5dc);
}

.stage-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
}

.stage-sub {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 9px;
}

.lifecycle-trust-strip {
  background: var(--f7-surface-container, #f5f5f5);
  border-radius: 12px;
  border: 1px solid var(--f7-list-item-border-color, #e0e0e0);
}
</style>